"use client";
import React from "react";
import { PaperComponent } from "@/components";
import Image from "next/image";
import {
  GIFS,
  IMAGES,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
} from "@/lib/constants";
import { LinearProgressBar } from "@/components/Progress";
import { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, CircularProgress, Fade } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
const STAGES = [
  {
    idle: "PAN Validation",
    processing: "PAN Validating",
    completed: "PAN Validated",
    caption: ({ pan }) => `PAN ${pan || ""}`,
  },
  {
    idle: "Pincode",
    processing: "Checking Pincode",
    completed: "Your Pincode is serviceable",
    caption: ({ pincode, location }) =>
      [pincode, location].filter(Boolean).join(" · "),
  },
  {
    idle: "Personal Details",
    processing: "Checking Personal Details",
    completed: "Personal Details Validated",
    caption: () => "Name, DOB & employment matched",
  },
  {
    idle: "Final Offer",
    processing: "Preparing your final Offer",
    completed: "Your final Offer is ready",
    caption: () => "Best available rate locked for you",
  },
];
const GREEN = "#159F36";

export default function OfferVerificationLoader({
  appId,
  complete = false,
  onComplete,
  pan = "",
  pincode = "",
  location = "",
}) {
  const storageKey = `offer-loader-step-${appId}`;
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(null);
  const completed = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    if (!appId) return;

    const savedStep = Number(sessionStorageUtil.get(storageKey) ?? 0);

    const step = Number.isInteger(savedStep)
      ? Math.min(3, Math.max(0, savedStep))
      : 0;

    const restoredElapsed = step * 2000;

    startedAt.current = performance.now() - restoredElapsed;
    setElapsed(restoredElapsed);
  }, [appId, storageKey]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (complete) return;

    if (startedAt.current === null) {
      startedAt.current = performance.now();
    }

    const timer = window.setInterval(() => {
      const elapsedTime = performance.now() - startedAt.current;
      const stepIndex = Math.min(3, Math.floor(elapsedTime / 2000));

      sessionStorageUtil.set(storageKey, String(stepIndex));

      if (elapsedTime >= 6000) {
        setElapsed(6000);
        window.clearInterval(timer);
      } else {
        setElapsed(elapsedTime);
      }
    }, 2000);

    return () => window.clearInterval(timer);
  }, [complete]);

  useEffect(() => {
    if (!complete || completed.current) return;
    completed.current = true;

    const timer = window.setTimeout(() => {
      sessionStorageUtil.remove(storageKey);
      onCompleteRef.current?.();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [complete]);

  // Even when the network takes five minutes, only the last item stays active.
  const activeIndex = complete ? 4 : Math.min(3, Math.floor(elapsed / 2000));
  const marks = [10, 40, 60, 90];
  const percentage = complete
    ? 100
    : elapsed >= 6000
      ? 90
      : Math.round(
          marks[Math.floor(elapsed / 2000)] +
            (marks[Math.floor(elapsed / 2000) + 1] -
              marks[Math.floor(elapsed / 2000)]) *
              ((elapsed % 2000) / 2000),
        );
  const details = { pan, pincode, location };
  return (
    <>
      <Box display={"flex"} justifyContent={"center"} alignContent={"center"}>
        {/* <LoaderComponent open={isLoading} /> */}
        <PaperComponent
          elevation={0}
          paperStyle={{
            backgroundColor: "rgba(242, 247, 247, 0.6)",
            border: "1px solid rgba(240, 240, 240, 0.6)",
            width: "100%",
            // maxWidth: "950px",
          }}
        >
          <Stack
            direction={"row"}
            marginY={"10px"}
            width={"full"}
            justifyContent={"center"}
          >
            <Box
              sx={{
                width: { xs: "186px", md: "240px" },
                height: { xs: "100px", md: "100px" },
                position: "relative",
              }}
            >
              <Image
                src={GIFS.pillerCoinRotating}
                unoptimized
                alt="note"
                fill
                style={{ objectFit: "contain" }}
              />
            </Box>
          </Stack>

          <Box width={"100%"} display={"flex"} justifyContent={"center"}>
            <Stack>
              <Typography
                fontWeight={{ md: 700, xs: 500 }}
                fontSize={{ xs: "14px", sm: "18px" }}
                color={WEB_COLORS.darkTeal}
                textAlign="center"
              >
                Your Loan{" "}
                <Typography
                  component="span"
                  fontWeight={{ xs: 500, md: 700 }}
                  fontSize={{ xs: "14px", sm: "18px" }}
                  color={WEB_COLORS.fluorescentGreen}
                  display="inline-block"
                >
                  Made Simple
                </Typography>
              </Typography>

              <Box
                mt={1}
                sx={{
                  width: "100%",
                  maxWidth: "450px",
                  mx: "auto",
                  textAlign: "center",
                }}
              >
                <Typography
                  fontWeight={{ xs: 400 }}
                  fontSize={{ xs: "14px" }}
                  color={WEB_COLORS.fadeColor}
                >
                  Just a few checks to get you closer to your loan offer
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: "358px",
              mx: "auto",
              mt: 2,
              mb: 2,
            }}
          >
            <LinearProgressBar value={percentage} />
          </Box>
          <Box
            sx={{ width: "100%", maxWidth: 354, mx: "auto" }}
            role="status"
            aria-label={`Offer verification ${percentage}%`}
          >
            <Stack spacing={0}>
              {STAGES.map((stage, index) => {
                const done = complete || index < activeIndex;
                const active = !complete && index === activeIndex;
                const pending = !done && !active;
                return (
                  <Box
                    key={stage.idle}
                    sx={{ position: "relative", pb: index === 3 ? 0 : "14px" }}
                  >
                    {index < 3 && (
                      <Box
                        aria-hidden="true"
                        sx={{
                          position: "absolute",
                          left: 23,
                          bottom: -1,
                          width: 2,
                          height: 16,
                          bgcolor: done ? GREEN : "#DDE5DF",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        minHeight: 64,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "13px",
                        px: "13px",
                        py: "11px",
                        gap: "12px",
                        bgcolor: active ? "#E5F9E9" : "#fff",
                        border: "1px solid",
                        borderColor: active || done ? "#20AB39" : "#EDF0ED",
                        transition:
                          "background-color 180ms, border-color 180ms",
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 22,
                          height: 22,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        {done ? (
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              bgcolor: GREEN,
                              display: "grid",
                              placeItems: "center",
                            }}
                          >
                            <CheckRoundedIcon
                              sx={{ color: "#fff", fontSize: 15 }}
                            />
                          </Box>
                        ) : active ? (
                          <CircularProgress
                            size={22}
                            thickness={2.2}
                            sx={{ color: GREEN }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 22,
                              width: 22,
                              borderRadius: "50%",
                              border: "2px solid #E0E8E2",
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: pending ? "#9EA9A2" : "#074A37",
                          }}
                        >
                          {done
                            ? stage.completed
                            : active
                              ? stage.processing
                              : stage.idle}
                        </Typography>
                        {!pending && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              lineHeight: 1.45,
                              mt: "2px",
                              color: "#929D96",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {stage.caption(details)}
                          </Typography>
                        )}
                      </Box>
                      {active && (
                        <Typography
                          sx={{
                            flexShrink: 0,
                            color: GREEN,
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          checking
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </PaperComponent>
      </Box>
    </>
  );
}
